import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useUploadStore } from "@components/FileUpload/uploadStore.ts";
import UnauthorizedRedirection from "@components/UnauthorizedRedirection/UnauthorizedRedirection.tsx";
import { useNotification } from "@context/NotificationContext.tsx";
import type { FormContext } from "@custom-types/form-types.ts";
import { useCheckOwnersMatter } from "@features/vessel-call/vessel-call-status/hooks/useCheckOwnersMatter.ts";
import { useCheckProposedAgency } from "@features/vessel-call/vessel-call-status/hooks/useCheckProposedAgency.ts";
import { useCurrentMovements } from "@features/vessel-call/vessel-call-status/hooks/useCurrentMovements.ts";
import { usePastMovements } from "@features/vessel-call/vessel-call-status/hooks/usePastMovements.ts";
import { useMovementService } from "@features/vessel-call/vessel-call-status/MovementServices/hooks/useMovementService.tsx";
import {
  getMovementServiceFormSchema,
  type MovementServiceFormData,
} from "@features/vessel-call/vessel-call-status/MovementServices/schema/movementServiceFormSchema.ts";
import {
  movementServiceFormDefaultValues,
  useMovementServiceFormStore,
  type MovementServiceFormState,
} from "@features/vessel-call/vessel-call-status/MovementServices/store/useMovementServiceForm.store.ts";
import type { VesselCallStatusContextType } from "@features/vessel-call/vessel-call-status/VesselCallStatus.tsx";
import { usePersistentForm } from "@hooks/usePersistentForm.ts";
import { useAuthStore } from "@persisted-state/auth/auth.store.ts";
import type { Movement } from "@services/movement-services/types.ts";
import { useQueryClient } from "@tanstack/react-query";
import { checkPermission } from "@utils/user-permissions.tsx";
import lodashSet from "lodash/set";
import { useTranslation } from "react-i18next";
import { Outlet, useNavigate, useOutletContext } from "react-router-dom";
import ApproveMovementService from "./form/ApproveMovementSevice";
import CancelMovementService from "./form/CancelMovementService";
import CloseMovementService from "./form/CloseMovementService";
import DisputeMovementService from "./form/DisputeMovementSevice";
import NonDisputeMovementService from "./form/NonDisputeMovementSevice";
import RejectMovementService from "./form/RejectMovementSevice";
import UpdateMovementService from "./form/updateMovement/UpdateMovementService.tsx";

const LOCAL_STORAGE_PAGE_KEY = "movement-services-page";
const LOCAL_STORAGE_PAGE_SIZE_KEY = "movement-services-page-size";

export type MovementOutletContext = FormContext<MovementServiceFormData> & {
  resetPersistedForm: () => void;
  setAll: (data: Partial<MovementServiceFormData>) => void;
  handleSubmit: () => void;
  cancel: () => void;
  handleOpenDrawer: (drawer: string) => void;
  vesselId: string;
  vesselCallId: string;
  vesselCallStatus: string;
  vesselCallDetails: VesselCallStatusContextType["vesselCallDetails"];
  draftAft?: number | null;
  draftFwd?: number | null;
  currentMovements: Movement[];
  pastMovementsData?: {
    items: Movement[];
    totalElements: number;
  };
  isPastMovementsLoading: boolean;
  isCurrentMovementsLoading: boolean;
  isMovementSubmitting: boolean;
  isMovementTransitioning: boolean;
  fakeStatus?: string | null;
  setFakeStatus?: (status: string | null) => void;
  ownersMatter?: boolean;
  proposedAgency?: boolean;
  pastMovementsPage: number;
  pastMovementsPageSize: number;
  setPastMovementsPagination: (page: number, pageSize: number) => void;
  selectPortReferenceCode?: string | null;
};

const pickFormValues = (state: MovementServiceFormState): MovementServiceFormData => {
  const {
    setField: _setField,
    getField: _getField,
    setAll: _setAll,
    resetForm: _resetForm,
    resetFieldsExcept: _resetFieldsExcept,
    ...formValues
  } = state;

  return formValues;
};

const buildDefaultMovementFormValues = (): MovementServiceFormData => ({
  ...movementServiceFormDefaultValues,
  additionalResources: [...(movementServiceFormDefaultValues.additionalResources ?? [])],
  additionalDocuments: [...(movementServiceFormDefaultValues.additionalDocuments ?? [])],
  h2s: [...(movementServiceFormDefaultValues.h2s ?? [])],
  pilotRequestNotification: [...(movementServiceFormDefaultValues.pilotRequestNotification ?? [])],
});

const service = "bff-vessel-call";

const MovementServices: React.FC = () => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const navigate = useNavigate();
  const clearUploadStore = useUploadStore((state) => state.clearAll);
  const canRequestApprovedMovement = useAuthStore((state) =>
    checkPermission(state.userActionPermissions, "canRequestApprovedMovementService"),
  );

  useEffect(() => {
    return () => {
      localStorage.removeItem(LOCAL_STORAGE_PAGE_KEY);
      localStorage.removeItem(LOCAL_STORAGE_PAGE_SIZE_KEY);
    };
  }, []);

  const [pastMovementsPage, setPastMovementsPage] = useState(() => {
    const saved = localStorage.getItem(LOCAL_STORAGE_PAGE_KEY);
    return saved ? parseInt(saved, 10) : 1;
  });

  const [pastMovementsPageSize, setPastMovementsPageSize] = useState(() => {
    const saved = localStorage.getItem(LOCAL_STORAGE_PAGE_SIZE_KEY);
    return saved ? parseInt(saved, 10) : 25;
  });
  const { vesselCallDetails, vesselRegisterDetails } =
    useOutletContext<VesselCallStatusContextType>();

  const {
    vesselId: registeredVesselId,
    id,
    draftAft,
    draftFwd,
    status: vesselCallStatus,
    selectPortReferenceCode,
  } = vesselCallDetails;
  const vesselId = registeredVesselId;
  const vesselCallId = id;
  const ownersMatter = useCheckOwnersMatter(vesselCallId);
  const proposedAgency = useCheckProposedAgency(vesselCallId);

  const { data: currentMovements = [], isLoading: isCurrentMovementsLoading } = useCurrentMovements(
    {
      service,
      vesselId,
      vesselCallId,
      staleTime: 0,
      refetchInterval: 60_000,
      refetchOnMount: true,
      refetchOnWindowFocus: true,
    },
  );

  const { data: pastMovementsData, isLoading: isPastMovementsLoading } = usePastMovements({
    service,
    vesselId,
    vesselCallId,
    page: pastMovementsPage - 1,
    size: pastMovementsPageSize,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });

  const queryClient = useQueryClient();
  const [isMovementSubmitting, setIsMovementSubmitting] = useState(false);
  const [isMovementTransitioning, setIsMovementTransitioning] = useState(false);
  const prevCurrentMovementsLengthRef = useRef(0);

  useEffect(() => {
    const prev = prevCurrentMovementsLengthRef.current;
    prevCurrentMovementsLengthRef.current = currentMovements.length;

    if (prev > 0 && currentMovements.length === 0) {
      setIsMovementTransitioning(true);
      queryClient
        .invalidateQueries({ queryKey: ["getPastMovementServices", vesselCallId, vesselId] })
        .then(() => setIsMovementTransitioning(false));
    }
  }, [currentMovements.length, queryClient, vesselCallId, vesselId]);

  const { setField, setAll, resetForm } = useMovementServiceFormStore();
  const initialStoreValues = pickFormValues(useMovementServiceFormStore.getState());
  const defaultFormValues = useMemo(() => buildDefaultMovementFormValues(), []);

  const form = usePersistentForm<MovementServiceFormData>({
    schema: getMovementServiceFormSchema(t, { canRequestApprovedMovement }),
    defaultValues: defaultFormValues,
    storeValues: {
      ...initialStoreValues,
    },
    setStoreValues: setAll,
    resetStore: resetForm,
    persistKey: "request-movement-service-form",
  });
  const { reset, clearErrors, watch, setError } = form;

  const clearForm = useCallback(() => {
    resetForm();
    reset(buildDefaultMovementFormValues());
  }, [resetForm, reset]);

  const updateFormData: MovementOutletContext["updateFormData"] = useCallback(
    (key, value) => {
      if (typeof key === "string" && key.includes(".")) {
        const currentStoreValues = pickFormValues(useMovementServiceFormStore.getState());
        const nextStoreValues = structuredClone(currentStoreValues);
        lodashSet(nextStoreValues as Record<string, unknown>, key, value);
        setAll(nextStoreValues);
        return;
      }

      const typedKey = key as keyof MovementServiceFormData;
      const typedValue = value as MovementServiceFormData[keyof MovementServiceFormData];

      setField(typedKey, typedValue);
    },
    [setAll, setField],
  );

  const currentMovementId = currentMovements[0]?.id;

  const { onSubmitMovementService } = useMovementService({
    formValues: useMovementServiceFormStore.getState(),
    resetForm: clearForm,
    onSettled: () => setIsMovementSubmitting(false),
    movementId: currentMovementId,
    vesselId: vesselId!,
    vesselCallId: vesselCallId!,
    vesselCallStatus,
    vesselRegisterDetails: vesselRegisterDetails,
  });

  const onValid = async (values: MovementServiceFormData) => {
    await onSubmitMovementService(values);
  };

  const onInvalid = (errors: any) => {
    setIsMovementSubmitting(false);
    console.error("MovementService validation errors", errors);
    notify({
      title: t("security-clearance.notifications.sc-request-submitted-error-title"),
      description: t("security-clearance.notifications.sc-request-submitted-error-description"),
      type: "error",
    });
  };

  const handleSubmit = () => {
    if (isMovementSubmitting) return;

    setIsMovementSubmitting(true);
    form.handleSubmit(onValid, onInvalid)();
  };

  const cancel = () => {
    clearForm();
    navigate(-1);
    clearUploadStore();
  };

  const [openDrawers, setOpenDrawers] = useState<{ [key: string]: boolean }>({
    updateDrawer: false,
    approveDrawer: false,
    nonDisputeDrawer: false,
    disputeDrawer: false,
    rejectDrawer: false,
    closeDrawer: false,
    cancelDrawer: false,
    delegateDrawer: false,
    addResourceDrawer: false, // this is a child drawer as it should open from within closeDrawer (see figma)
  });

  const handleOpenDrawer = (drawer: string) =>
    setOpenDrawers((prev) => ({ ...prev, [drawer]: true }));

  const handleCloseDrawer = (drawer: string) =>
    setOpenDrawers((prev) => ({ ...prev, [drawer]: false }));

  const [fakeStatus, setFakeStatus] = useState<string | null>(null);

  const setPastMovementsPagination = (page: number, pageSize: number) => {
    setPastMovementsPage(page);
    setPastMovementsPageSize(pageSize);
    localStorage.setItem(LOCAL_STORAGE_PAGE_KEY, String(page));
    localStorage.setItem(LOCAL_STORAGE_PAGE_SIZE_KEY, String(pageSize));
  };

  const outletCtx: MovementOutletContext = {
    formData: form,
    updateFormData,
    resetPersistedForm: clearForm,
    setAll,
    handleOpenDrawer,
    handleSubmit,
    cancel,
    vesselId: vesselId!,
    vesselCallId: vesselCallId!,
    vesselCallStatus: vesselCallStatus,
    vesselCallDetails,
    draftAft,
    draftFwd,
    clearErrors: clearErrors,
    setError,
    watch,
    currentMovements,
    pastMovementsData,
    isPastMovementsLoading,
    isCurrentMovementsLoading,
    isMovementSubmitting,
    isMovementTransitioning,
    fakeStatus,
    setFakeStatus,
    ownersMatter,
    proposedAgency,
    setPastMovementsPagination,
    pastMovementsPage,
    pastMovementsPageSize,
    selectPortReferenceCode,
  };

  if (ownersMatter) {
    return <UnauthorizedRedirection />;
  }
  return (
    <div className="mt-4 px-8 flex flex-col gap-6">
      <Outlet context={outletCtx} />
      <>
        <ApproveMovementService
          isOpen={openDrawers.approveDrawer}
          onClose={handleCloseDrawer}
          currentMovements={currentMovements}
        />
        <RejectMovementService
          isOpen={openDrawers.rejectDrawer}
          onClose={handleCloseDrawer}
          currentMovements={currentMovements}
        />
        <DisputeMovementService
          isOpen={openDrawers.disputeDrawer}
          onClose={handleCloseDrawer}
          currentMovements={currentMovements}
        />
        <NonDisputeMovementService
          isOpen={openDrawers.nonDisputeDrawer}
          onClose={handleCloseDrawer}
          currentMovements={currentMovements}
        />
        <UpdateMovementService
          isOpen={openDrawers.updateDrawer}
          onClose={handleCloseDrawer}
          currentMovements={currentMovements}
        />
        <CloseMovementService
          isOpen={openDrawers.closeDrawer}
          onClose={handleCloseDrawer}
          currentMovements={currentMovements}
        />
        <CancelMovementService
          isOpen={openDrawers.cancelDrawer}
          onClose={handleCloseDrawer}
          currentMovements={currentMovements}
        />
      </>
    </div>
  );
};

export default MovementServices;
